class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.backdrop = document.getElementById("cart-drawer-backdrop");
    this.isOpen = false;
    this.isReady = false;
    this.activeElement = null;
  }

  connectedCallback() {
    this.resetState();
    this.setupEventListeners();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add("is-ready");
        this.isReady = true;
      });
    });
  }

  resetState() {
    this.isOpen = false;
    this.classList.remove("active");

    if (this.backdrop) {
      this.backdrop.classList.remove("is-visible");
    }

    document.body.classList.remove("drawer-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    const header =
      document.querySelector("sticky-header") ||
      document.querySelector("header");
    if (header) {
      header.style.paddingRight = "";
    }
  }

  setupEventListeners() {
    this.addEventListener("keyup", (evt) => {
      if (evt.code === "Escape") this.close();
    });

    this.setupCartIconListener();
    this.setupCloseButton();
    this.setupBackdropListener();
    this.setupProductLinks();
    this.setupQuantityListeners();
    this.setupRemoveListeners();
  }

  setupCartIconListener() {
    const cartIcon = document.querySelector("#cart-icon-bubble");
    if (!cartIcon) return;

    if (window.location.pathname === "/cart") {
      cartIcon.style.pointerEvents = "none";
      cartIcon.style.opacity = "0.5";
      return;
    }

    cartIcon.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open();
    });
  }

  setupCloseButton() {
    const closeButton = this.querySelector(".drawer__close");
    if (!closeButton) return;

    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.close();
    });
  }

  setupBackdropListener() {
    if (!this.backdrop) return;

    this.backdrop.addEventListener("click", (event) => {
      event.preventDefault();
      this.close();
    });
  }

  setupProductLinks() {
    this.addEventListener("click", (e) => {
      const productLink = e.target.closest(
        ".brutalist-item-name, .brutalist-item-image a"
      );
      if (productLink) {
        this.classList.remove("is-ready");
        this.resetState();
      }
    });
  }

  setupQuantityListeners() {
    this.addEventListener("change", (e) => {
      const input = e.target.closest(".brutalist-quantity-input");
      if (!input) return;

      const index = input.dataset.index;
      const quantity = parseInt(input.value, 10);
      this.updateCartItem(index, quantity);
    });

    this.addEventListener("click", (e) => {
      const button = e.target.closest(".brutalist-quantity-button");
      if (!button) return;

      e.preventDefault();
      const quantitySelector = button.closest(".brutalist-quantity-selector");
      const input = quantitySelector?.querySelector(
        ".brutalist-quantity-input"
      );
      if (!input) return;

      const currentValue = parseInt(input.value, 10) || 0;
      const newValue =
        button.name === "plus"
          ? currentValue + 1
          : Math.max(0, currentValue - 1);

      if (newValue !== currentValue) {
        input.value = newValue;
        const index = input.dataset.index;
        this.updateCartItem(index, newValue);
      }
    });
  }

  setupRemoveListeners() {
    this.addEventListener("click", (e) => {
      const removeBtn = e.target.closest("cart-remove-button");
      if (!removeBtn) return;

      e.preventDefault();
      const index = removeBtn.dataset.index;
      this.updateCartItem(index, 0);
    });
  }

  async updateCartItem(index, quantity) {
    const item = this.querySelector(`#CartDrawer-Item-${index}`);
    const loadingOverlay = item?.querySelector(".loading-overlay");

    if (loadingOverlay) loadingOverlay.classList.remove("hidden");

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line: index, quantity: quantity }),
      });

      if (!response.ok) throw new Error("Failed to update cart");

      await this.fetchAndRenderCart();
    } catch (error) {
      console.error("Error updating cart:", error);
      if (loadingOverlay) loadingOverlay.classList.add("hidden");
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const header =
      document.querySelector("sticky-header") ||
      document.querySelector("header");
    if (header) {
      header.style.paddingRight = `${scrollbarWidth}px`;
    }

    if (this.backdrop) {
      this.backdrop.classList.add("is-visible");
    }

    requestAnimationFrame(() => {
      this.classList.add("active");
      document.body.classList.add("drawer-open");
    });

    this.dispatchEvent(new CustomEvent("drawerOpen", { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove("active");
    document.body.classList.remove("drawer-open");

    if (this.backdrop) {
      this.backdrop.classList.remove("is-visible");
    }

    setTimeout(() => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      const header =
        document.querySelector("sticky-header") ||
        document.querySelector("header");
      if (header) {
        header.style.paddingRight = "";
      }
    }, 400);

    this.dispatchEvent(new CustomEvent("drawerClose", { bubbles: true }));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(parsedState) {
    this.fetchAndRenderCart();
    this.open();
  }

  async fetchAndRenderCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    }
  }

  renderCart(cart) {
    const cartItemsContainer = document.getElementById("CartDrawer-CartItems");
    const cartFooter = document.getElementById("CartDrawer-Footer");
    const cartDrawerItems = this.querySelector("cart-drawer-items");

    if (cart.item_count === 0) {
      if (cartDrawerItems) cartDrawerItems.classList.add("is-empty");
      if (cartItemsContainer) cartItemsContainer.innerHTML = "";
      if (cartFooter) cartFooter.innerHTML = "";
      this.updateCartIcon(0);
      return;
    }

    if (cartDrawerItems) cartDrawerItems.classList.remove("is-empty");

    if (cartItemsContainer) {
      cartItemsContainer.innerHTML = `
        <div class="brutalist-cart-items">
          ${cart.items
            .map((item, index) => this.renderCartItem(item, index + 1))
            .join("")}
        </div>
      `;
    }

    if (cartFooter) {
      cartFooter.innerHTML = `
        <div class="brutalist-cart-summary">
          <div class="brutalist-summary-row">
            <span class="brutalist-summary-label">${
              window.theme?.strings?.subtotal || "SUMA CZĘŚCIOWA"
            }</span>
            <span class="brutalist-drawer-summary-value">${this.formatMoney(
              cart.total_price
            )}</span>
          </div>
        </div>

        <div class="brutalist-cart-footer-note">
          <p>${
            window.theme?.strings?.cart_taxes_shipping ||
            "Podatki i koszty wysyłki obliczone przy kasie"
          }</p>
        </div>

        <div class="brutalist-cart-footer-actions">
          <form action="/cart" method="post" id="CartDrawer-CheckoutForm">
            <button
              type="submit"
              name="checkout"
              class="brutalist-checkout-btn brutalist-checkout-btn-primary"
              form="CartDrawer-CheckoutForm">
              ${window.theme?.strings?.checkout || "PRZEJDŹ DO KASY"}
            </button>
          </form>
          
          <a href="/cart" class="brutalist-checkout-btn brutalist-checkout-btn-secondary">
            ${window.theme?.strings?.view_cart || "PRZEJDŹ DO KOSZYKA"}
          </a>
        </div>
      `;
    }

    this.updateCartIcon(cart.item_count);
  }

  renderCartItem(item, index) {
    const hasVariants =
      item.variant_title && item.variant_title !== "Default Title";
    const optionsHTML = hasVariants
      ? `<div class="brutalist-item-options">
          ${
            item.options_with_values
              ? item.options_with_values
                  .map(
                    (option) => `
            <div class="brutalist-item-option">
              <span class="brutalist-option-label">${option.name}:</span>
              <span class="brutalist-option-value">${option.value}</span>
            </div>
          `
                  )
                  .join("")
              : `
            <div class="brutalist-item-option">
              <span class="brutalist-option-label">Wariant:</span>
              <span class="brutalist-option-value">${item.variant_title}</span>
            </div>
          `
          }
        </div>`
      : "";

    const priceHTML =
      item.original_price !== item.final_price
        ? `<span class="brutalist-price-original">${this.formatMoney(
            item.original_price
          )}</span>
           <span class="brutalist-price-final">${this.formatMoney(
             item.final_price
           )}</span>`
        : `<span class="brutalist-price-final">${this.formatMoney(
            item.final_price
          )}</span>`;

    return `
      <div id="CartDrawer-Item-${index}" class="brutalist-cart-item">
        <div class="loading-overlay hidden">
          <div class="loading-spinner"></div>
        </div>

        <div class="brutalist-item-image">
          <a href="${item.url}">
            ${
              item.image
                ? `<img class="brutalist-item-img" src="${this.getSizedImageUrl(
                    item.image,
                    "300x"
                  )}" alt="${
                    item.title
                  }" loading="lazy" width="140" height="140">`
                : ""
            }
          </a>
        </div>

        <div class="brutalist-item-content">
          <div class="brutalist-item-details">
            <a href="${item.url}" class="brutalist-item-name">${
      item.product_title
    }</a>
            ${optionsHTML}
            <div class="brutalist-item-price">${priceHTML}</div>
          </div>

          <div class="brutalist-item-actions">
            <div class="brutalist-quantity-selector">
              <button class="brutalist-quantity-button" name="minus" type="button" aria-label="Zmniejsz ilość">−</button>
              <input class="brutalist-quantity-input" type="number" name="updates[]" value="${
                item.quantity
              }" min="0" aria-label="Ilość" id="Drawer-Quantity-${index}" data-index="${index}">
              <button class="brutalist-quantity-button" name="plus" type="button" aria-label="Zwiększ ilość">+</button>
            </div>

            <cart-remove-button data-index="${index}">
              <button type="button" class="brutalist-remove-btn" aria-label="Usuń produkt">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M4 5H14M6 5V4C6 3.44772 6.44772 3 7 3H11C11.5523 3 12 3.44772 12 4V5M13 5V14C13 14.5523 12.5523 15 12 15H6C5.44772 15 5 14.5523 5 14V5H13Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </cart-remove-button>

            <div class="brutalist-drawer-item-total">${this.formatMoney(
              item.final_line_price
            )}</div>
          </div>
        </div>
      </div>
    `;
  }

  getSizedImageUrl(url, size) {
    if (!url) return "";
    const match = url.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i
    );
    if (match) {
      const prefix = url.split(match[0])[0];
      const suffix = match[0];
      return `${prefix}_${size}${suffix}`;
    }
    return url;
  }

  formatMoney(cents) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      "{{amount}} PLN";
    const amount = cents / 100;

    let value = "";
    const formatString =
      moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";

    switch (formatString) {
      case "amount":
        value = amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_no_decimals":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_with_comma_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_with_space_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      case "amount_no_decimals_with_space_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      default:
        value = amount.toFixed(2);
    }

    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  updateCartIcon(itemCount) {
    const cartBubble = document.querySelector(
      "#cart-icon-bubble .cart-count-bubble"
    );

    if (cartBubble) {
      if (itemCount > 0) {
        cartBubble.textContent = itemCount > 99 ? "99+" : itemCount;
        cartBubble.style.display = "flex";
      } else {
        cartBubble.textContent = "";
        cartBubble.style.display = "none";
      }
    }
  }
}

customElements.define("cart-drawer", CartDrawer);

function ensureCartDrawerClosed() {
  const drawer = document.querySelector("cart-drawer");
  const backdrop = document.getElementById("cart-drawer-backdrop");

  if (drawer) {
    drawer.classList.remove("active", "is-ready");
    drawer.isOpen = false;
  }

  if (backdrop) {
    backdrop.classList.remove("is-visible");
  }

  document.body.classList.remove("drawer-open");
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";

  const header =
    document.querySelector("sticky-header") || document.querySelector("header");
  if (header) {
    header.style.paddingRight = "";
  }

  if (drawer) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        drawer.classList.add("is-ready");
      });
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  ensureCartDrawerClosed();
});

window.addEventListener("pageshow", () => {
  ensureCartDrawerClosed();
});
